import type { HTMLAttributeAnchorTarget } from 'react';
import type { FluentIcon } from '@fluentui/react-icons';
import type { AccessType } from '@modules/access/types';
import type { TItem, TItemType, TResolvedItemNames } from './items.types';

export type TSparseItem = {
  _id: string;
  name: string;
};

export type TItemCardStatus = {
  openApprovals: number;
  overduePeriodicReviews: number;
};

export type TJurisdictionMap = Record<TSparseItem['_id'], TSparseItem['name']>;

export type TPathItem = TSparseItem & {
  accessRight: AccessType;
  disabled?: boolean;
  // If an explicit href is present, use it:
  href?: string;
  target?: Extract<HTMLAttributeAnchorTarget, '_blank' | '_self'>;
  // Icon to display in the path item
  icon?: FluentIcon;
  // A synthetic path item for a non-folder target has no folder to read this from.
  folderType?: EFolderType;
};
export type TPath = TPathItem[];

// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/folders/types/folderType.types.ts
export enum EFolderType {
  NORMAL = 'NORMAL',
  BLOCK = 'BLOCK',
  PRODUCT = 'PRODUCT',
  JURISDICTION = 'JURISDICTION',
  QM = 'QM',
  COUNTRY = 'COUNTRY',
  SNAPSHOT = 'SNAPSHOT',
  LITERATURE = 'LITERATURE',
  PROJECTS = 'PROJECTS',
  CHANGE_TEMPLATES = 'CHANGE_TEMPLATES',
}

export type TCountryRegistration = {
  products: string[];
};

export type TRootChild = TSparseItem & {
  folderType: EFolderType;
};

export type TFolderReturn = {
  _id: string;
  name: string;
  isQm: boolean;
  resolvedPath: TPath;
  accessRight: AccessType;
  accessRightAllowFrozen: AccessType;
  isProcessing: boolean;
  folderType: EFolderType;
  isFrozen: boolean;
  children: TItem[];
  recentItems: TItem[];
  pendingApprovals: TItem[];
  rootChildren: TRootChild[];
  resolvedNames: TResolvedItemNames;
  cardStatuses: Record<string, TItemCardStatus>;
  pullPreference?: string;
};

export type IFolder = TFolderReturn & {
  resolvedNames: TResolvedItemNames;
  jurisdictions: TSparseItem[];
  blocksFolder?: TSparseItem;
  snapshotsFolder?: TSparseItem;
};

export enum ECountriesFolderType {
  ROOT = 'ROOT',
  COUNTRY = 'COUNTRY',
}

export enum ESnapshotsFolderType {
  ROOT = 'ROOT',
  SNAPSHOT_COLLECTION = 'SNAPSHOT_COLLECTION',
  // SNAPSHOT = 'SNAPSHOT',
}

export type TRootItems = {
  children: TItem[];
  recentItems: TItem[];
  // May hold ids that are no longer in `children`.
  recentProductIds: string[];
  pendingApprovals: TItem[];
  productSubmissionInfos: TProductSubmissionInfo[];
  numRecentApprovedQmItems: number;
  numQmAccess: number;
  numCountries: number;
  numCountryRegistrations: number;
  cardStatuses: Record<string, TItemCardStatus>;
};

export const isTFolder = (data: TRootItems | IFolder | TFolderReturn): data is IFolder | TFolderReturn =>
  Boolean((data as TFolderReturn)._id && (data as TFolderReturn).folderType);

export type TProductSubmissionInfo = {
  _id: string;
  product: string;
  dueDate: string;
};

export type TSelectFolderDialogState = {
  isOpen: boolean;
  affectedItems: TItem[];
  select: (targetId: string) => Promise<void>;
};

export type TOnMove = (sourceId: string, itemType: TItemType, targetId: string) => void;
